document.documentElement.classList.replace("no-js", "js");

// Native links and forms remain usable if JavaScript is unavailable.
document.addEventListener("click", async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  const imageLink = target.closest("[data-gallery-image]");
  if (imageLink && !event.metaKey && !event.ctrlKey && event.button === 0) {
    const gallery = imageLink.closest(".product-gallery");
    const mainLink = gallery?.querySelector(".main-product-image");
    const image = mainLink?.querySelector("img");
    if (image) {
      event.preventDefault();
      image.src = imageLink.dataset.galleryImage;
      image.removeAttribute("srcset");
      image.alt = imageLink.dataset.alt || "";
      mainLink.href = imageLink.href;
    }
  }
  const optionLink = target.closest("[data-option-link]");
  if (
    !optionLink ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    event.button !== 0
  )
    return;
  const product = optionLink.closest("[data-product-section]");
  if (!product || product.getAttribute("aria-busy") === "true") return;
  const destination = new URL(optionLink.href);
  if (destination.origin !== location.origin) return;
  event.preventDefault();
  product.setAttribute("aria-busy", "true");
  try {
    const request = new URL(destination);
    request.searchParams.set("section_id", product.dataset.productSection);
    const response = await fetch(request, {
      signal: AbortSignal.timeout(10000),
    });
    if (!response.ok) throw new Error("Product unavailable");
    const html = new DOMParser().parseFromString(
      await response.text(),
      "text/html",
    );
    const replacement = html.querySelector("[data-product-section]");
    if (
      !replacement ||
      replacement.dataset.productSection !== product.dataset.productSection
    )
      throw new Error("Invalid section");
    // Only server-rendered markup from this store is inserted. No client prices.
    product.replaceWith(replacement);
    history.replaceState({}, "", destination);
    const selected = [
      ...replacement.querySelectorAll("[data-option-link]"),
    ].find((link) => link.href === destination.href);
    selected?.focus({ preventScroll: true });
    // Keep Shopify-generated structured data aligned with the selected variant.
    const oldData = replacement.parentElement?.querySelector(
      'script[type="application/ld+json"]',
    );
    const newData = html.querySelector('script[type="application/ld+json"]');
    if (oldData && newData) oldData.textContent = newData.textContent;
  } catch {
    // Full navigation is the reliable fallback for stale sections/network failures.
    location.assign(destination.href);
  } finally {
    product.removeAttribute("aria-busy");
  }
});

// Category tabs switch server-rendered panels. Without JavaScript they link to the collections.
document.addEventListener("click", (event) => {
  const tab =
    event.target instanceof Element ? event.target.closest("[data-tab]") : null;
  if (
    !tab ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    event.button !== 0
  )
    return;
  const section = tab.closest("[data-collection-tabs]");
  const panels = [...(section?.querySelectorAll("[data-tab-panel]") ?? [])];
  const panel = panels.find((p) => p.dataset.tabPanel === tab.dataset.tab);
  if (!panel) return;
  event.preventDefault();
  section.querySelectorAll("[data-tab]").forEach((other) => {
    const active = other === tab;
    other.classList.toggle("active", active);
    other.setAttribute("aria-selected", String(active));
  });
  panels.forEach((other) => {
    other.hidden = other !== panel;
  });
  const count = section.querySelector("[data-tab-count]");
  if (count)
    count.textContent =
      count.dataset.template.replace("__COUNT__", panel.dataset.count) + " ↓";
  section.querySelectorAll("[data-tab-link]").forEach((link) => {
    link.href = panel.dataset.url;
    const text = link.querySelector("[data-tab-link-text]");
    if (text)
      text.textContent = link.dataset.template.replace(
        "__COUNT__",
        panel.dataset.count,
      );
  });
});

// Cart drawer: Shopify renders the drawer section; the browser only swaps markup.
const cartDrawer = document.querySelector("[data-cart-drawer]");
const cartFormData = (form) => {
  const data = {};
  for (const [key, value] of new FormData(form)) {
    const property = key.match(/^properties\[(.+)\]$/);
    if (property) (data.properties ??= {})[property[1]] = value;
    else data[key] = value;
  }
  return data;
};
async function cartRequest(url, body) {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/javascript",
    },
    body: JSON.stringify({ ...body, sections: "cart-drawer" }),
  });
  const data = await response.json();
  if (!response.ok || data.status)
    throw new Error(
      data.description || data.message || cartDrawer.dataset.error,
    );
  return data;
}
function renderCart(sections) {
  const fresh = new DOMParser()
    .parseFromString(sections?.["cart-drawer"] ?? "", "text/html")
    .querySelector("[data-cart-drawer]");
  if (!fresh) return;
  cartDrawer.innerHTML = fresh.innerHTML;
  cartDrawer.dataset.itemCount = fresh.dataset.itemCount;
  document.querySelectorAll("[data-cart-count]").forEach((count) => {
    count.textContent = fresh.dataset.itemCount;
  });
}
if (cartDrawer) {
  document.addEventListener("click", async (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest("[data-cart-open]")) {
      event.preventDefault();
      if (!cartDrawer.open) cartDrawer.showModal();
      return;
    }
    if (
      event.target.closest("[data-cart-close]") ||
      event.target === cartDrawer
    ) {
      cartDrawer.close();
      return;
    }
    const change = event.target.closest("[data-cart-change]");
    if (!change) return;
    cartDrawer.setAttribute("aria-busy", "true");
    try {
      const data = await cartRequest(cartDrawer.dataset.changeUrl, {
        line: Number(change.dataset.cartChange),
        quantity: Number(change.dataset.quantity),
      });
      renderCart(data.sections);
    } catch (error) {
      const message = cartDrawer.querySelector("[data-cart-message]");
      if (message) {
        message.textContent = error.message;
        message.hidden = false;
      }
    } finally {
      cartDrawer.removeAttribute("aria-busy");
    }
  });
  document.addEventListener("submit", async (event) => {
    const form = event.target;
    if (
      !(form instanceof HTMLFormElement) ||
      !new URL(form.action, location.href).pathname.endsWith("/cart/add")
    )
      return;
    event.preventDefault();
    const button = form.querySelector('[name="add"]');
    const message = form.querySelector("[data-form-message]");
    if (message) message.hidden = true;
    button?.setAttribute("aria-busy", "true");
    try {
      const data = await cartRequest(form.action, cartFormData(form));
      renderCart(data.sections);
      if (!cartDrawer.open) cartDrawer.showModal();
    } catch (error) {
      if (message) {
        message.textContent = error.message;
        message.hidden = false;
      }
    } finally {
      button?.removeAttribute("aria-busy");
    }
  });
}

// Desktop menus open on hover; click and keyboard keep the native <details> behaviour.
const hoverNav = matchMedia("(hover: hover) and (min-width: 761px)");
const desktopMenus = [
  ...document.querySelectorAll(".desktop-nav .nav-dropdown"),
];
const closeTimers = new Map();
desktopMenus.forEach((menu) => {
  menu.addEventListener("mouseenter", () => {
    clearTimeout(closeTimers.get(menu));
    if (!hoverNav.matches) return;
    // One menu at a time: entering a menu replaces the previous one immediately.
    desktopMenus.forEach((other) => {
      if (other !== menu) {
        clearTimeout(closeTimers.get(other));
        other.open = false;
      }
    });
    menu.open = true;
  });
  menu.addEventListener("mouseleave", () => {
    if (!hoverNav.matches) return;
    closeTimers.set(
      menu,
      setTimeout(() => {
        menu.open = false;
      }, 180),
    );
  });
  menu.querySelector("summary")?.addEventListener("click", (event) => {
    if (hoverNav.matches && menu.open) event.preventDefault();
  });
});

document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape") return;
  const opened = [
    ...document.querySelectorAll(
      ".site-header details[open], .filter-group[open]",
    ),
  ];
  const focusTarget = opened.find((detail) =>
    detail.contains(document.activeElement),
  );
  opened.forEach((detail) => {
    detail.open = false;
  });
  focusTarget?.querySelector("summary")?.focus();
});
document.addEventListener("click", (event) => {
  if (
    !(event.target instanceof Element) ||
    event.target.closest(".site-header, .filter-group")
  )
    return;
  document
    .querySelectorAll(".site-header details[open], .filter-group[open]")
    .forEach((detail) => {
      detail.open = false;
    });
});
